import orderBy from 'lodash/orderBy';
import {
    RepresentativeCreateForm,
    RepresentativeStatus,
    RepresentativeUpdateForm,
} from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { patientService } from '@patient/services/patient/patientService';
import { RepresentativeApi } from '@api/RepresentativeApi';
import { RepresentativeType } from 'chaika-enums';

class RepresentativeService {
    public async load(): Promise<void> {
        const ehr = store.ehr;

        if (!ehr.representativeList.isDataSet && !ehr.confidantList.isDataSet) {
            const [representatives, confidants] = await Promise.all([
                RepresentativeApi.getEhrRepresentativeList(ehr.id),
                RepresentativeApi.getConfidantList(ehr.id),
            ]);

            const representativeIds = representatives.flatMap(({ ehrId, representativeId }) => [
                representativeId,
                ehrId,
            ]);
            const confidantIds = confidants.flatMap(({ representativeId, ehrId }) => [ehrId, representativeId]);

            await patientService.loadByIds([...representativeIds, ...confidantIds]);

            ehr.representativeList.set(orderBy(representatives, 'updateTime', 'asc'));
            ehr.confidantList.set(confidants);
        }
    }

    public async create(params: RepresentativeCreateForm): Promise<void> {
        const ehr = store.ehr;

        const createParams = {
            status: RepresentativeStatus.NotConfirmed,
            type: RepresentativeType.Other,
            ...params,
        };

        await Promise.all([
            RepresentativeApi.create(ehr.id, createParams),
            patientService.loadByIds([createParams.representativeId]),
        ]);

        ehr.representativeList.add([
            {
                ...createParams,
                ehrId: ehr.id,
                creationTime: new Date().toISOString(),
                updateTime: new Date().toISOString(),
                isDeleted: false,
            },
        ]);
    }

    public async update(representativeId: string, params: RepresentativeUpdateForm): Promise<void> {
        const ehr = store.ehr;
        const current = ehr.representativeList.get(representativeId);
        await RepresentativeApi.update(current?.ehrId ?? ehr.id, representativeId, params);

        ehr.representativeList.update(representativeId, params);
    }

    public async remove(representativeId: string): Promise<void> {
        const ehr = store.ehr;
        const current = ehr.representativeList.get(representativeId);

        await RepresentativeApi.remove(current?.ehrId ?? ehr.id, representativeId);
        ehr.representativeList.remove(representativeId);
    }
}

export const representativeService = new RepresentativeService();
